import React from 'react';
import { FJ110 } from '../../../services/j201Api';
import { dateToInput, inputToDate, fmt, getApprovalBadge, INVOICE_TYPE_OPTIONS } from '../helpers';

interface J201FormFieldsProps {
  formData: Partial<FJ110>;
  names: Record<string, string>;
  isEditing: boolean;
  mode: string;
  getField: (key: keyof FJ110) => FJ110[keyof FJ110];
  setField: (key: keyof FJ110, value: FJ110[keyof FJ110]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const J201FormFields: React.FC<J201FormFieldsProps> = React.memo(({
  formData, names, isEditing, mode, getField, setField, resolveSingleName,
}) => {
  const disabled = !isEditing;
  const badge = getApprovalBadge(formData.fj110_80);

  return (
    <div className="j201-tab1">
      {/* Section 1: 基本資訊 */}
      <div className="form-section">
        <span className="section-tag">基本資訊</span>
        <div className="form-grid form-grid-4col">
          <label data-contract-field="fj110_01">申請單號</label>
          <input type="text" readOnly value={String(getField('fj110_01') || '')} className="field-readonly" />

          <label data-contract-field="fj110_80">核准狀態</label>
          <span className={`badge ${badge.cls}`}>{badge.text}</span>

          <label data-contract-field="fj110_11">申請日期</label>
          <input
            type="date"
            disabled={disabled}
            value={dateToInput(getField('fj110_11') as string | null)}
            onChange={e => setField('fj110_11', inputToDate(e.target.value) as FJ110[keyof FJ110])}
          />

          <label className="required" data-contract-field="fj110_13">需要日期</label>
          <input
            type="date"
            disabled={disabled}
            value={dateToInput(getField('fj110_13') as string | null)}
            onChange={e => setField('fj110_13', inputToDate(e.target.value) as FJ110[keyof FJ110])}
          />

          <label className="required" data-contract-field="fj110_12">客戶代號</label>
          <div className="lookup-field">
            <input
              type="text" maxLength={12}
              disabled={disabled}
              value={String(getField('fj110_12') || '')}
              onChange={e => setField('fj110_12', e.target.value)}
              onBlur={() => resolveSingleName('customer', 'customer', String(getField('fj110_12') || ''))}
            />
            <span className="lookup-name">{names.customer || ''}</span>
          </div>

          <label data-contract-field="fj110_25">客戶單號</label>
          <textarea
            rows={2} disabled={disabled}
            value={String(getField('fj110_25') || '')}
            onChange={e => setField('fj110_25', e.target.value)}
          />

          <label className="required" data-contract-field="fj110_21">業務代號</label>
          <div className="lookup-field">
            <input
              type="text" maxLength={8}
              disabled={disabled}
              value={String(getField('fj110_21') || '')}
              onChange={e => setField('fj110_21', e.target.value)}
              onBlur={() => {
                resolveSingleName('salesperson', 'employee', String(getField('fj110_21') || ''));
              }}
            />
            <span className="lookup-name">{names.salesperson || ''}</span>
          </div>

          <label data-contract-field="fj110_22">部門代號</label>
          <div className="lookup-field">
            <input
              type="text" readOnly
              value={String(getField('fj110_22') || '')}
              className="field-readonly"
            />
            <span className="lookup-name">{names.department || ''}</span>
          </div>

          <label data-contract-field="fj110_91">入單人員</label>
          <div className="lookup-field">
            <input
              type="text" readOnly
              value={String(getField('fj110_91') || '')}
              className="field-readonly"
            />
            <span className="lookup-name">{names.entryPerson || ''}</span>
          </div>
        </div>
      </div>

      {/* Section 2: 幣別與發票 */}
      <div className="form-section">
        <span className="section-tag">幣別與發票</span>
        <div className="form-grid form-grid-4col">
          <label className="required" data-contract-field="fj110_14">幣別</label>
          <div className="lookup-field">
            <input
              type="text" maxLength={3}
              disabled={disabled}
              value={String(getField('fj110_14') || '')}
              onChange={e => setField('fj110_14', e.target.value)}
              onBlur={() => resolveSingleName('currency', 'currency', String(getField('fj110_14') || ''))}
              style={{ width: 80 }}
            />
            <span className="lookup-name">{names.currency || ''}</span>
          </div>

          <label data-contract-field="fj110_15">匯率</label>
          <span className="field-amount" style={{ width: 100 }}>
            {((getField('fj110_15') as number) || 0).toFixed(4)}
          </span>

          <label className="required" data-contract-field="fj110_171">發票種類</label>
          <select
            disabled={disabled}
            value={getField('fj110_171') as number || 1}
            onChange={e => setField('fj110_171', parseInt(e.target.value, 10))}
            style={{ width: 180 }}
          >
            {INVOICE_TYPE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <label data-contract-field="fj110_16">收費</label>
          <input
            type="checkbox"
            disabled={disabled}
            checked={!!getField('fj110_16')}
            onChange={e => setField('fj110_16', e.target.checked as FJ110[keyof FJ110])}
          />

          <label data-contract-field="fj110_33">扣庫存</label>
          <input
            type="checkbox"
            disabled={disabled}
            checked={!!getField('fj110_33')}
            onChange={e => setField('fj110_33', e.target.checked as FJ110[keyof FJ110])}
          />
        </div>
      </div>

      {/* Section 3: 金額彙總 (display only) */}
      <div className="form-section">
        <span className="section-tag">金額彙總</span>
        <div className="form-grid form-grid-4col">
          <label data-contract-field="fj110_18">原幣未稅總額</label>
          <span className="field-amount">{fmt(formData.fj110_18)}</span>

          <label data-contract-field="fj110_181">原幣含稅總額</label>
          <span className="field-amount">{fmt(formData.fj110_181)}</span>

          <label data-contract-field="fj110_19">稅額</label>
          <span className="field-amount">{fmt(formData.fj110_19)}</span>

          <label data-contract-field="twd_untaxed_total">臺幣未稅總額</label>
          <span className="field-amount">
            {fmt((formData.fj110_18 || 0) * (formData.fj110_15 || 0))}
          </span>

          <label data-contract-field="fj110_90">版本</label>
          <span>{formData.fj110_90 || 0}</span>
        </div>
      </div>

      {/* Section 4: 備註 */}
      <div className="form-section">
        <span className="section-tag">備註</span>
        <div className="form-grid">
          <label data-contract-field="fj110_30">備註代號</label>
          <div className="lookup-field">
            <input
              type="text" maxLength={2}
              disabled={disabled}
              value={String(getField('fj110_30') || '')}
              onChange={e => setField('fj110_30', e.target.value)}
              onBlur={() => resolveSingleName('remarkTemplate', 'remarkTemplate', String(getField('fj110_30') || ''))}
              style={{ width: 60 }}
            />
            <span className="lookup-name">{names.remarkTemplate || ''}</span>
          </div>

          <label data-contract-field="fj110_31">備註說明</label>
          <textarea
            rows={4} disabled={disabled}
            value={String(getField('fj110_31') || '')}
            onChange={e => setField('fj110_31', e.target.value)}
            style={{ maxWidth: 500 }}
          />
        </div>
      </div>
    </div>
  );
});

J201FormFields.displayName = 'J201FormFields';
export default J201FormFields;
